export interface ViewportSize {
  width: number;
  height: number;
}

export interface SafeArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ViewportLayout {
  baseWidth: number;
  baseHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  displayWidth: number;
  displayHeight: number;
  offsetX: number;
  offsetY: number;
  scale: number;
  safeArea: SafeArea;
  orientation: 'landscape' | 'portrait';
  shouldRecommendLandscape: boolean;
}

const MIN_VIEWPORT_SIZE = 1;
const MOBILE_WIDTH_THRESHOLD = 768;

export function calculateViewportLayout(
  baseResolution: ViewportSize,
  viewportSize: ViewportSize
): ViewportLayout {
  const baseWidth = Math.max(MIN_VIEWPORT_SIZE, baseResolution.width);
  const baseHeight = Math.max(MIN_VIEWPORT_SIZE, baseResolution.height);
  const viewportWidth = Math.max(MIN_VIEWPORT_SIZE, viewportSize.width);
  const viewportHeight = Math.max(MIN_VIEWPORT_SIZE, viewportSize.height);
  const scale = Math.min(viewportWidth / baseWidth, viewportHeight / baseHeight);
  const displayWidth = roundCssPixel(baseWidth * scale);
  const displayHeight = roundCssPixel(baseHeight * scale);
  const orientation = viewportWidth >= viewportHeight ? 'landscape' : 'portrait';
  const safeArea = calculateSafeArea(
    { width: baseWidth, height: baseHeight },
    viewportWidth,
    viewportHeight,
    scale
  );

  return {
    baseWidth,
    baseHeight,
    viewportWidth,
    viewportHeight,
    displayWidth,
    displayHeight,
    offsetX: roundCssPixel((viewportWidth - displayWidth) / 2),
    offsetY: roundCssPixel((viewportHeight - displayHeight) / 2),
    scale,
    safeArea,
    orientation,
    shouldRecommendLandscape: orientation === 'portrait' && viewportWidth < MOBILE_WIDTH_THRESHOLD
  };
}

function calculateSafeArea(
  baseResolution: ViewportSize,
  viewportWidth: number,
  viewportHeight: number,
  scale: number
): SafeArea {
  const targetCssMarginX = viewportWidth < MOBILE_WIDTH_THRESHOLD ? 12 : 24;
  const targetCssMarginY = viewportHeight < 480 ? 10 : 20;
  const marginX = clamp(Math.round(targetCssMarginX / scale), 32, 72);
  const marginY = clamp(Math.round(targetCssMarginY / scale), 28, 56);

  return {
    x: marginX,
    y: marginY,
    width: baseResolution.width - marginX * 2,
    height: baseResolution.height - marginY * 2
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function roundCssPixel(value: number): number {
  return Math.round(value * 100) / 100;
}
